import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { hasDraftScores } from '../../utils/scoreDraft';
import { gameRemoval } from './gameRemoval';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOURCES = [
  ['skins', 'Skins', '/skins/events', 'events'],
  ['match-play', 'Match Play', '/match-play/events', 'events'],
  ['stroke-play', 'Stroke Play', '/stroke-play/events', 'events'],
  ['group', 'Group rounds', '/group-rounds/my-rounds?status=in_progress', 'rounds'],
  ['solo', 'Solo round', '/scores/drafts', 'drafts'],
];

export const useInProgressGames = userId => {
  const [state, setState] = useState({ games: [], counts: {}, errors: [], loading: true });
  const [revision, setRevision] = useState(0);
  const sequence = useRef(0);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  const removeLocal = useCallback(game => setState(previous => ({ ...previous,
    games: previous.games.filter(item => item.key !== game.key),
    counts: { ...previous.counts, [game.type]: Math.max(0, (previous.counts[game.type] || 0) - 1) },
  })), []);
  useEffect(() => {
    const request = ++sequence.current;
    const controller = new AbortController();
    setState({ games: [], counts: {}, errors: [], loading: true });
    Promise.allSettled(SOURCES.map(async ([type, label, path, field]) => {
      const { data } = await axios.get(`${API}${path}`, { signal: controller.signal, timeout: 20000 });
      if (field && !Array.isArray(data?.[field])) throw new Error('Invalid game list');
      if (!Array.isArray(data?.[field])) throw new Error('Invalid game list');
      return { type, label, records: type === 'solo' ? data[field].filter(hasDraftScores) : data[field] };
    })).then(results => {
      if (controller.signal.aborted || request !== sequence.current) return;
      const games = [], counts = {}, errors = [];
      results.forEach((result, index) => {
        if (result.status === 'rejected') { errors.push(SOURCES[index][1]); return; }
        const { type, label, records } = result.value;
        counts[type] = records.length;
        records.forEach(record => {
          if (!record || (type !== 'solo' && record.status !== (type === 'group' ? 'in_progress' : 'active'))) return;
          const id = type === 'solo' ? record.draft_id : type === 'group' ? record.round_id : record.event_id;
          if (!id || record.legacy_read_only) return;
          const href = type === 'solo' ? `/add-score?resume=solo&draft=${encodeURIComponent(id)}` : type === 'group'
            ? `/group-round/${encodeURIComponent(id)}` : `/tournaments/${type}?event=${encodeURIComponent(id)}`;
          games.push({ key: `${type}-${id}`, type, label, href,
            ...gameRemoval(record, type, id, userId),
            name: record.name || record.course_name || label,
            course: record.course_name, holes: record.number_of_holes || record.holes,
            played: type === 'solo' ? record.hole_scores.filter(score => Number(score) > 0).length : null });
        });
      });
      setState({ games, counts, errors, loading: false });
    });
    return () => controller.abort();
  }, [userId, revision]);
  return { ...state, refresh, removeLocal };
};